import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { Chargement, EnTetePage, Etiquette, Indicateur, Message } from "@cabinet/charte";
import { dossiersApi, ErreurApi, type DossierVue, type ExerciceVue } from "../api/client";
import { formaterDate } from "../domaine/formats";
import { useSession } from "../session";

const LIBELLE_STATUT: Record<string, string> = {
  OUVERT: "Ouvert",
  EN_REVISION: "En révision",
  CLOTURE: "Clôturé",
  ARCHIVE: "Archivé",
};

export function PageDossier() {
  const { id = "" } = useParams();

  const dossier = useQuery({
    queryKey: ["dossier", id],
    queryFn: () => dossiersApi.consulter(id),
  });

  const exercices = useQuery({
    queryKey: ["exercices", id],
    queryFn: () => dossiersApi.exercices(id),
  });

  const erreur = dossier.error ?? exercices.error;

  if (erreur != null) {
    return (
      <>
        <EnTetePage titre="Dossier" />
        <Message type="erreur" titre={erreur instanceof ErreurApi ? erreur.titre : "Erreur"}>
          <p style={{ margin: "6px 0 0" }}>{erreur.message}</p>
        </Message>
      </>
    );
  }

  if (dossier.isPending || dossier.data == null) return <Chargement quoi="le dossier" />;

  return (
    <>
      <EnTetePage
        surTitre={dossier.data.formeJuridique ?? dossier.data.pays}
        titre={dossier.data.raisonSociale}
        description={`${dossier.data.regimeDImpositionLibelle} · référentiel ${dossier.data.codeDuReferentiel} · tenue en ${dossier.data.devise}`}
      />

      <FicheIdentite dossier={dossier.data} />

      <Exercices dossier={id} exercices={exercices.data ?? []} enCours={exercices.isPending} />
    </>
  );
}

function FicheIdentite({ dossier }: { dossier: DossierVue }) {
  const identifiants = dossier.identifiants;
  const lignes: Array<[string, string | null]> =
    identifiants.pays === "MA"
      ? [
          ["ICE", identifiants.ice],
          ["Identifiant fiscal", identifiants.identifiantFiscal],
          ["Registre de commerce", identifiants.registreDeCommerce],
          ["Taxe professionnelle", identifiants.taxeProfessionnelle],
          ["Affiliation CNSS", identifiants.affiliationCnss],
        ]
      : [
          ["SIREN", identifiants.siren],
          ["SIRET", identifiants.siret],
          ["TVA intracommunautaire", identifiants.tvaIntracommunautaire],
          ["Code APE", identifiants.codeApe],
        ];

  return (
    <div className="carte">
      <h2>Identité</h2>

      <div className="grille">
        <Indicateur libelle="Statut" valeur={dossier.statut} />
        <Indicateur
          libelle="TVA"
          valeur={dossier.tva.assujetti ? (dossier.tva.periodicite ?? "—") : "Non assujetti"}
          detail={
            dossier.tva.assujetti
              ? `${dossier.tva.regime} · ${
                  dossier.tva.deductionIntegrale
                    ? "déduction intégrale"
                    : `prorata ${dossier.tva.prorataDeDeduction} %`
                }`
              : undefined
          }
        />
        <Indicateur libelle="Devise de tenue" valeur={dossier.devise} />
      </div>

      <div className="tableau-conteneur">
        <table>
          <thead>
            <tr>
              <th>Identifiant</th>
              <th>Valeur</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map(([libelle, valeur]) => (
              <tr key={libelle}>
                <td>{libelle}</td>
                <td className="nombre">{valeur ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * Exercices du dossier.
 *
 * Les transitions proposées viennent du serveur : c'est lui qui sait dans quel
 * état un exercice peut passer, et l'écran ne réinvente pas la règle.
 */
function Exercices({
  dossier,
  exercices,
  enCours,
}: {
  dossier: string;
  exercices: ExerciceVue[];
  enCours: boolean;
}) {
  const cache = useQueryClient();
  const { peut } = useSession();
  const [erreur, setErreur] = useState<string | null>(null);
  const [ouvertureVisible, setOuvertureVisible] = useState(false);

  const rafraichir = () => {
    void cache.invalidateQueries({ queryKey: ["exercices", dossier] });
  };
  const signaler = (echec: unknown) =>
    setErreur(echec instanceof ErreurApi ? echec.message : "L'opération n'a pas abouti.");

  const transition = useMutation({
    mutationFn: ({ id, statut, motif }: { id: string; statut: string; motif?: string }) =>
      dossiersApi.changerLeStatut(id, statut, motif),
    onSuccess: rafraichir,
    onError: signaler,
  });

  const ouverture = useMutation({
    mutationFn: ({ debut, fin }: { debut: string; fin: string }) =>
      dossiersApi.ouvrirUnExercice(dossier, debut, fin),
    onSuccess: () => {
      rafraichir();
      setOuvertureVisible(false);
    },
    onError: signaler,
  });

  function demanderLaTransition(exercice: ExerciceVue, cible: string) {
    setErreur(null);
    // La réouverture d'un exercice clôturé se justifie : le motif part dans la
    // piste d'audit et devra pouvoir être expliqué lors d'un contrôle (RG-08).
    if (exercice.statut === "CLOTURE" && cible === "EN_REVISION") {
      const motif = window.prompt("Motif de la réouverture :");
      if (motif == null || motif.trim() === "") return;
      transition.mutate({ id: exercice.id, statut: cible, motif });
      return;
    }
    transition.mutate({ id: exercice.id, statut: cible });
  }

  const prochainDebut = exercices.length > 0 ? suivantDe(exercices[exercices.length - 1].fin) : "";

  return (
    <div className="carte">
      <div className="barre-actions">
        <h2 style={{ margin: 0 }}>Exercices</h2>
        <span className="espace" />
        {peut("CREER_DOSSIER") && (
          <button type="button" onClick={() => setOuvertureVisible((v) => !v)}>
            {ouvertureVisible ? "Annuler" : "Ouvrir un exercice"}
          </button>
        )}
      </div>

      {erreur != null && <Message type="erreur">{erreur}</Message>}

      {ouvertureVisible && (
        <form
          className="champs"
          style={{ marginBottom: 16 }}
          onSubmit={(e) => {
            e.preventDefault();
            setErreur(null);
            const champs = new FormData(e.currentTarget);
            ouverture.mutate({
              debut: String(champs.get("debut")),
              fin: String(champs.get("fin")),
            });
          }}
        >
          <div className="champ">
            <label htmlFor="debut">Premier jour</label>
            <input id="debut" name="debut" type="date" defaultValue={prochainDebut} required />
            {prochainDebut !== "" && (
              <div className="aide">Au lendemain de la clôture précédente, sans quoi un trou apparaîtrait.</div>
            )}
          </div>
          <div className="champ">
            <label htmlFor="fin">Dernier jour</label>
            <input id="fin" name="fin" type="date" required />
          </div>
          <div className="champ" style={{ alignSelf: "end" }}>
            <button type="submit" className="principal" disabled={ouverture.isPending}>
              Ouvrir
            </button>
          </div>
        </form>
      )}

      {enCours && <Chargement quoi="les exercices" />}

      {!enCours && exercices.length === 0 && (
        <p className="discret-texte">Aucun exercice ouvert sur ce dossier.</p>
      )}

      {exercices.length > 0 && (
        <div className="tableau-conteneur">
          <table>
            <thead>
              <tr>
                <th>Exercice</th>
                <th>Du</th>
                <th>Au</th>
                <th className="nombre">Mois</th>
                <th>Statut</th>
                <th>Périodes closes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {exercices.map((exercice) => (
                <tr key={exercice.id}>
                  <td>{exercice.libelle}</td>
                  <td>{formaterDate(exercice.debut)}</td>
                  <td>{formaterDate(exercice.fin)}</td>
                  <td className="nombre">{exercice.dureeEnMois}</td>
                  <td>
                    <Etiquette variante={varianteDuStatut(exercice.statut)}>
                      {exercice.statutLibelle}
                    </Etiquette>
                  </td>
                  <td className="discret-texte">
                    {exercice.verrouilleJusquAu != null
                      ? `jusqu'au ${formaterDate(exercice.verrouilleJusquAu)}`
                      : "—"}
                  </td>
                  <td>
                    <div className="jetons">
                      {peut("CLOTURER_EXERCICE") &&
                        exercice.transitionsPossibles.map((cible) => (
                          <button
                            key={cible}
                            type="button"
                            className="tertiaire"
                            disabled={transition.isPending}
                            onClick={() => demanderLaTransition(exercice, cible)}
                          >
                            {LIBELLE_STATUT[cible] ?? cible}
                          </button>
                        ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function varianteDuStatut(statut: string) {
  switch (statut) {
    case "OUVERT":
      return "succes" as const;
    case "EN_REVISION":
      return "avertissement" as const;
    case "CLOTURE":
      return "info" as const;
    default:
      return "neutre" as const;
  }
}

/** Lendemain d'une date au format ISO, sans dépendance à un fuseau. */
function suivantDe(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}
